// Couche d'accès aux données : une implémentation DÉMO (mémoire) et une implémentation LIVE (Supabase).
// deno-lint-ignore-file no-explicit-any
import type { AssetPlan, Brand, Campaign, Person } from '../../supabase/functions/_shared/core/index.ts';
import { explainStorageError, installMail, installSender, installTargets, pickCampaign, type ResyncEntry } from '../../supabase/functions/_shared/core/index.ts';
import { ASSET_CACHE_CONTROL, renderSignature } from '../../supabase/functions/_shared/core/index.ts';
import { demoBrands, demoCampaigns, demoPeople } from '../../supabase/functions/_shared/core/fixtures.ts';
import { config, isDemo, publicBaseUrl } from './config.ts';

const demoAssets = config.demoAssets ?? {};

export interface PushResult {
  person: string;
  sendAs?: string;
  action:
    | 'pushed'
    | 'would_push'
    | 'unchanged'
    | 'skipped'
    | 'error'
    | 'too_long';
  campaign?: string | null;
  length?: number;
  warnings?: string[];
  error?: string;
}
export interface InstallMailResult {
  person: string;
  name?: string;
  action: 'would_send' | 'sent' | 'error';
  subject?: string;
  link?: string;
  messageId?: string;
  error?: string;
}
export interface InstallMailReport {
  runId: number;
  dryRun: boolean;
  /** Expéditeur réel : la personne connectée qui a déclenché l'envoi, nom tiré de l'annuaire. */
  sender?: { email: string; name: string };
  stats: Record<string, number>;
  results: InstallMailResult[];
  sample?: { to: string; subject: string; text: string; html: string } | null;
}
export interface ResyncReport {
  runId: number;
  dryRun: boolean;
  stats: { directory: number; people: number; fields: number; applied: number; errors: number };
  plan: ResyncEntry[];
  applied: string[];
  errors: { email: string; error: string }[];
}
export interface ClickStats {
  total: number;
  first_at: string | null;
  last_at: string | null;
  d7: number;
  d7_prev: number;
  d30: number;
  d30_prev: number;
  by_campaign: { campaign_id: string | null; name: string | null; n: number }[];
  by_brand: { brand_id: string | null; name: string | null; n: number }[];
  top_people: { person_id: string; email: string | null; name: string | null; n: number }[];
  daily: { day: string; n: number }[];
}
export interface AutomationStatus {
  auto_push: boolean;
  pg_cron: boolean;
  cron_jobs: number;
}
export interface EdgeHit {
  day: string;
  route: 'a' | 'b' | 'go';
  cached: boolean;
  n: number;
}
export interface Run {
  id: number;
  kind: 'sync' | 'push' | 'install_mail' | 'resync';
  dry_run: boolean;
  triggered_by: string | null;
  /** Liens d'installation : adresse expéditrice réelle. */
  sender?: string | null;
  started_at: string;
  finished_at: string | null;
  stats: Record<string, number> | null;
  error: string | null;
}
export interface SigState {
  send_as_email: string;
  person_id: string | null;
  campaign_id: string | null;
  pushed_at: string | null;
  last_error: string | null;
}
export interface InstallPayload {
  name: string;
  signatures: { sendAs: string; html: string; text: string; brand: string }[];
}

export interface Api {
  demo: boolean;
  currentUser(): Promise<string | null>;
  signInWithGoogle(): Promise<void>;
  signInWithEmail(email: string): Promise<void>;
  signInWithPassword(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  listBrands(): Promise<Brand[]>;
  /** Enregistre la fiche (identité, images, coordonnées, styles). N'écrit JAMAIS `layout_tree` : il appartient à l'éditeur. */
  saveBrand(b: Brand): Promise<void>;
  /** Enregistre la mise en page seule : aucune autre colonne réécrite (une fiche enregistrée entre-temps n'est pas écrasée). */
  saveBrandLayout(id: string, layout_tree: Brand['layout_tree']): Promise<void>;
  listPeople(): Promise<Person[]>;
  savePerson(p: Person, isNew?: boolean): Promise<void>;
  listCampaigns(): Promise<Campaign[]>;
  saveCampaign(c: Campaign, isNew?: boolean): Promise<void>;
  deleteCampaign(id: string): Promise<void>;
  /** Envoie un visuel préparé par `planAsset` (cœur) et renvoie son URL publique sur notre domaine. */
  uploadAsset(plan: AssetPlan): Promise<string>;
  sync(dryRun: boolean): Promise<any>;
  /** « Reprendre depuis Google » : plan champ par champ (dryRun) puis application ; `only` = adresses visées (sinon toutes). */
  resync(opts: { dryRun: boolean; only?: string[] }): Promise<ResyncReport>;
  push(
    opts: { dryRun?: boolean; only?: string[]; force?: boolean },
  ): Promise<{ stats: Record<string, number>; results: PushResult[] }>;
  listRuns(): Promise<Run[]>;
  listStates(): Promise<SigState[]>;
  clicksByCampaign(): Promise<Record<string, number>>;
  getAutoPush(): Promise<boolean>;
  setAutoPush(on: boolean): Promise<void>;
  /** État réel de l'automatisation : interrupteur et tâches planifiées (aucune tant que pg_cron n'est pas installé). */
  automationStatus(): Promise<AutomationStatus>;
  /** Liens d'installation par mail : simulation nominative (dryRun), test sur soi (only), envoi réel. Admin seulement. */
  sendInstallLinks(opts: { dryRun?: boolean; only?: string[] }): Promise<InstallMailReport>;
  /** Appels des routes publiques /a/, /b/, /go/ par jour (compteur des Pages Functions). */
  listEdgeHits(days?: number): Promise<EdgeHit[]>;
  /** Statistiques des clics, calculées par la fonction SQL click_stats(). */
  clickStats(): Promise<ClickStats>;
  install(token: string): Promise<InstallPayload>;
}

const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x));
export const uuid = () => crypto.randomUUID();

// ------------------------------------------------------------------ DÉMO
class DemoApi implements Api {
  demo = true;
  private brands = clone(demoBrands).map((b) => (b.id === 'bjbs' ? { ...b, logo_url: demoAssets.logo ?? b.logo_url } : b));
  /** En démo, une bannière permanente (campagne sans dates) sur BONJOUR/BONSOIR montre le rendu « mode local ». */
  private permanent: Campaign = {
    ...clone(demoCampaigns[1]),
    id: '10000000-0000-4000-8000-00000000000a',
    name: 'Bannière permanente — BONJOUR/BONSOIR',
    image_url: demoAssets.defaultBanner ?? demoCampaigns[1].image_url,
    link_url: 'https://bonjourbonsoir.paris/',
    alt: 'Bonjour/Bonsoir',
    starts_at: null,
    ends_at: null,
    brand_ids: ['bjbs'],
    priority: 0,
    utm_auto: true,
  };
  private people = clone(demoPeople);
  private campaigns = [
    // Une campagne toujours en cours, pour que la démo montre une bannière active dès l'ouverture.
    {
      ...clone(demoCampaigns[0]),
      id: '10000000-0000-4000-8000-000000000009',
      name: 'Démo — Rentrée du groupe',
      alt: 'La saison reprend',
      link_url: 'https://bonjourbonsoir.paris/',
      brand_ids: [] as string[],
      image_url: demoAssets.campaigns?.[2] ??
        'https://placehold.co/1200x300/png?text=RENTREE+2026',
      starts_at: new Date(Date.now() - 5 * 864e5).toISOString(),
      ends_at: new Date(Date.now() + 25 * 864e5).toISOString(),
    },
    ...clone(demoCampaigns).map((c, i) => ({
      ...c,
      image_url: demoAssets.campaigns?.[i] ?? c.image_url,
    })),
    this.permanent,
  ];
  private runs: Run[] = [];
  private states = new Map<string, SigState & { hash: string }>();

  async currentUser() {
    return 'demo@bonjourbonsoir.paris';
  }
  async signInWithGoogle() {}
  async signInWithEmail() {}
  async signInWithPassword() {}
  async signOut() {}
  async listBrands() {
    return clone(this.brands);
  }
  async saveBrand(b: Brand) {
    if (b.is_default) this.brands.filter((x) => x.is_default && x.id !== b.id).forEach((x) => (x.is_default = false));
    const i = this.brands.findIndex((x) => x.id === b.id);
    if (i >= 0) this.brands[i] = { ...clone(b), layout_tree: this.brands[i].layout_tree };
    else this.brands.push(clone(b));
  }
  async saveBrandLayout(id: string, layout_tree: Brand['layout_tree']) {
    const b = this.brands.find((x) => x.id === id);
    if (!b) throw new Error('Charte introuvable');
    b.layout_tree = clone(layout_tree);
  }
  async listPeople() {
    return clone(this.people);
  }
  async savePerson(p: Person) {
    const i = this.people.findIndex((x) => x.id === p.id);
    if (i >= 0) this.people[i] = clone(p);
    else this.people.push(clone(p));
  }
  async listCampaigns() {
    return clone(this.campaigns);
  }
  async saveCampaign(c: Campaign) {
    const next = { ...clone(c), updated_at: new Date().toISOString() };
    const i = this.campaigns.findIndex((x) => x.id === c.id);
    if (i >= 0) this.campaigns[i] = next;
    else this.campaigns.push(next);
  }
  async deleteCampaign(id: string) {
    this.campaigns = this.campaigns.filter((c) => c.id !== id);
  }
  async uploadAsset(plan: AssetPlan) {
    return URL.createObjectURL(new Blob([plan.bytes as BlobPart], { type: plan.contentType })); // aperçu local uniquement
  }
  async resync(opts: { dryRun: boolean; only?: string[] }): Promise<ResyncReport> {
    // Démo : Google « connaît » un autre nom de famille pour la première personne de la liste.
    const wanted = opts.only?.map((e) => e.toLowerCase());
    const targets = this.people.filter((p) => p.kind === 'person' && (!wanted || wanted.includes(p.primary_email.toLowerCase()))).slice(0, 1);
    const plan: ResyncEntry[] = targets.filter((p) => p.last_name !== 'Corrigé').map((p) => ({ id: p.id, email: p.primary_email, changes: [{ field: 'last_name', label: 'Nom', from: p.last_name, to: 'Corrigé' }] }));
    if (!opts.dryRun) for (const e of plan) { const p = this.people.find((x) => x.id === e.id)!; p.last_name = 'Corrigé'; }
    return { runId: 0, dryRun: opts.dryRun, stats: { directory: this.people.length, people: plan.length, fields: plan.length, applied: opts.dryRun ? 0 : plan.length, errors: 0 }, plan, applied: opts.dryRun ? [] : plan.map((e) => e.email), errors: [] };
  }
  async sync(dryRun: boolean) {
    const stats = {
      directory: this.people.length,
      created: 0,
      known: this.people.length,
      deactivated: 0,
      suspended: 0,
      restored: 0,
      reactivated: 0,
    };
    this.runs.unshift({
      id: this.runs.length + 1,
      kind: 'sync',
      dry_run: dryRun,
      triggered_by: 'démo',
      started_at: new Date().toISOString(),
      finished_at: new Date().toISOString(),
      stats,
      error: null,
    });
    return { dryRun, stats, note: 'Mode démo : aucun appel à Google.' };
  }
  async push(opts: { dryRun?: boolean; only?: string[]; force?: boolean }) {
    const results: PushResult[] = [];
    for (const p of this.people) {
      if (
        !p.active || !p.managed ||
        (opts.only && !opts.only.includes(p.primary_email))
      ) continue;
      const r = renderSignature({
        person: p,
        sendAsEmail: p.primary_email,
        brands: this.brands,
        campaigns: this.campaigns,
      }, {
        mode: 'gmail',
        publicBaseUrl,
      });
      const prev = this.states.get(p.primary_email);
      if (!opts.force && prev?.hash === r.html) {
        results.push({
          person: p.primary_email,
          sendAs: p.primary_email,
          action: 'unchanged',
          campaign: r.campaign?.name ?? null,
        });
        continue;
      }
      if (!opts.dryRun) {
        this.states.set(p.primary_email, {
          send_as_email: p.primary_email,
          person_id: p.id,
          campaign_id: r.campaign?.id ?? null,
          pushed_at: new Date().toISOString(),
          last_error: null,
          hash: r.html,
        });
      }
      results.push({
        person: p.primary_email,
        sendAs: p.primary_email,
        action: opts.dryRun ? 'would_push' : 'pushed',
        campaign: r.campaign?.name ?? null,
        length: r.length,
        warnings: r.warnings,
      });
    }
    const stats = results.reduce<Record<string, number>>(
      (a, r) => ((a[r.action] = (a[r.action] ?? 0) + 1), a),
      {},
    );
    this.runs.unshift({
      id: this.runs.length + 1,
      kind: 'push',
      dry_run: !!opts.dryRun,
      triggered_by: 'démo',
      started_at: new Date().toISOString(),
      finished_at: new Date().toISOString(),
      stats,
      error: null,
    });
    return { stats, results };
  }
  async listRuns() {
    return clone(this.runs);
  }
  async listStates() {
    return [...this.states.values()].map(({ hash: _h, ...s }) => s);
  }
  async clicksByCampaign() {
    return {};
  }
  private autoPush = false;
  async getAutoPush() {
    return this.autoPush;
  }
  async setAutoPush(on: boolean) {
    this.autoPush = on;
  }
  async automationStatus() {
    return { auto_push: this.autoPush, pg_cron: false, cron_jobs: 0 };
  }
  async sendInstallLinks(opts: { dryRun?: boolean; only?: string[] }) {
    // Démo : l'expéditeur est la personne « connectée » si elle est dans l'annuaire, sinon la première personne.
    const me = await this.currentUser();
    const who = installSender(this.people, me ?? '');
    const sender = who.ok ? who.sender : { email: me ?? 'demo@bonjourbonsoir.paris', name: 'Prénom Nom — Bonjour/Bonsoir (démo)' };
    const targets = installTargets(this.people, opts.only ?? null);
    const results: InstallMailResult[] = targets.map((p) => {
      const m = installMail(p, publicBaseUrl, { name: sender.name });
      return { person: p.primary_email, name: [p.first_name, p.last_name].filter(Boolean).join(' ') || p.primary_email, action: opts.dryRun ? 'would_send' : 'sent', subject: m.subject, link: m.text.split('\n').find((l) => l.startsWith('http')) };
    });
    const first = targets[0];
    return { runId: 0, dryRun: !!opts.dryRun, sender, stats: { [opts.dryRun ? 'would_send' : 'sent']: results.length }, results, sample: first ? installMail(first, publicBaseUrl, { name: sender.name }) : null };
  }
  async clickStats(): Promise<ClickStats> {
    // Démo : trois clics de test, comme en production au lancement — l'état « presque vide » doit être lisible.
    const d = (h: number) => new Date(Date.now() - h * 3600e3).toISOString();
    return { total: 3, first_at: d(50), last_at: d(46), d7: 3, d7_prev: 0, d30: 3, d30_prev: 0, by_campaign: [{ campaign_id: null, name: null, n: 3 }], by_brand: [{ brand_id: 'bjbs', name: 'BONJOUR/BONSOIR', n: 3 }], top_people: [{ person_id: 'p1', email: 'prenom.n@bonjourbonsoir.paris', name: 'Prénom Nom', n: 3 }], daily: [{ day: d(48).slice(0, 10), n: 3 }] };
  }
  async listEdgeHits(days = 14) {
    const out: EdgeHit[] = [];
    for (let i = 0; i < days; i++) {
      const day = new Date(Date.now() - i * 864e5).toISOString().slice(0, 10);
      out.push({ day, route: 'a', cached: true, n: 900 + i * 37 }, { day, route: 'b', cached: false, n: 210 + i * 5 }, { day, route: 'b', cached: true, n: 1400 + i * 20 }, { day, route: 'go', cached: false, n: 24 + (i % 5) });
    }
    return out;
  }
  async install(token: string): Promise<InstallPayload> {
    const p = this.people.find((x) => x.install_token === token);
    if (!p) {
      throw new Error(
        'Lien invalide (démo : essayez #/install/demo-direction)',
      );
    }
    const addresses = [...new Set([p.primary_email, ...p.send_as_emails])];
    return {
      name: [p.first_name, p.last_name].filter(Boolean).join(' ') ||
        p.display_name || p.primary_email,
      signatures: addresses.map((sendAs) => {
        const r = renderSignature(
          {
            person: p,
            sendAsEmail: sendAs,
            brands: this.brands,
            campaigns: [],
          },
          { mode: 'local', publicBaseUrl },
        );
        // Démo : pas d'endpoint /b/ ici, on montre l'image de la campagne retenue au lieu de l'URL dynamique.
        const live = pickCampaign(this.campaigns, { brandId: r.brand.id, emails: [p.primary_email, sendAs], noBanner: p.no_banner }, new Date());
        if (live) r.html = r.html.replace(/src="[^"]*\/b\/[^"]+"/, `src="${live.image_url}"`);
        return { sendAs, html: r.html, text: r.text, brand: r.brand.name };
      }),
    };
  }
}

// ------------------------------------------------------------------ LIVE (Supabase)
const SUPABASE_ESM = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2/+esm';

class LiveApi implements Api {
  demo = false;
  private sb: any;
  private constructor(sb: any) {
    this.sb = sb;
  }
  static async create(): Promise<LiveApi> {
    const mod: any = await import(SUPABASE_ESM);
    const sb = mod.createClient(config.supabaseUrl, config.supabaseAnonKey, {
      auth: {
        flowType: 'pkce',
        detectSessionInUrl: true,
        persistSession: true,
      },
    });
    return new LiveApi(sb);
  }
  private async q<T>(p: Promise<{ data: T; error: any }>): Promise<T> {
    const { data, error } = await p;
    if (error) throw new Error(error.message);
    return data;
  }
  async currentUser() {
    const { data } = await this.sb.auth.getSession();
    return data.session?.user?.email ?? null;
  }
  async signInWithGoogle() {
    await this.sb.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: location.origin + '/' },
    });
  }
  async signInWithPassword(email: string, password: string) {
    const { error } = await this.sb.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      throw new Error(
        error.message === 'Invalid login credentials' ? 'Adresse ou mot de passe incorrect' : error.message,
      );
    }
  }
  async signInWithEmail(email: string) {
    const { error } = await this.sb.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: location.origin + '/',
        shouldCreateUser: false,
      },
    });
    if (error) throw new Error(error.message);
  }
  async signOut() {
    await this.sb.auth.signOut();
  }
  listBrands() {
    return this.q<Brand[]>(
      this.sb.from('brands').select('*').order('is_default', {
        ascending: false,
      }).order('name'),
    );
  }
  async saveBrand(b: Brand) {
    // Une seule charte par défaut : on ne retire le drapeau QUE sur celles qui l'ont (et pas sur
    // celle qu'on enregistre). Réécrire toutes les autres lignes brouillerait leurs horodatages.
    if (b.is_default) {
      await this.q(
        this.sb.from('brands').update({ is_default: false }).eq('is_default', true).neq('id', b.id),
      );
    }
    // Colonnes que la fiche n'édite pas : jamais réécrites depuis sa copie en mémoire (une mise en page
    // terminée entre-temps dans un autre onglet resterait sinon écrasée par l'ancienne).
    const { updated_at: _u, layout_tree: _l, ...row } = b as Brand & { updated_at?: string };
    await this.q(this.sb.from('brands').upsert(row));
  }
  async saveBrandLayout(id: string, layout_tree: Brand['layout_tree']) {
    await this.q(this.sb.from('brands').update({ layout_tree }).eq('id', id));
  }
  listPeople() {
    return this.q<Person[]>(
      this.sb.from('people').select('*').order('primary_email'),
    );
  }
  async savePerson(p: Person, isNew = false) {
    const row: Record<string, unknown> = { ...p };
    // Champs gérés par la base ou par la synchro Google : jamais écrits depuis le back-office.
    for (
      const k of [
        'updated_at',
        'google_id',
        'google_suspended',
        'source',
        'last_synced_at',
        'public_token',
      ]
    ) delete row[k];
    if (isNew) delete row.install_token; // jetons générés par la base
    await this.q(
      isNew ? this.sb.from('people').insert(row) : this.sb.from('people').update(row).eq('id', p.id),
    );
  }
  listCampaigns() {
    return this.q<Campaign[]>(
      this.sb.from('campaigns').select('*').order('starts_at', {
        ascending: false,
      }),
    );
  }
  async saveCampaign(c: Campaign, isNew = false) {
    const { updated_at: _u, ...row } = c as Campaign & { created_at?: string };
    delete (row as Record<string, unknown>).created_at;
    await this.q(
      isNew ? this.sb.from('campaigns').insert(row) : this.sb.from('campaigns').update(row).eq('id', c.id),
    );
  }
  async deleteCampaign(id: string) {
    await this.q(this.sb.from('campaigns').delete().eq('id', id));
  }
  async uploadAsset(plan: AssetPlan) {
    // Les octets du plan partent tels quels : aucune transformation ici, ni pour un GIF ni pour le reste.
    // Une erreur du stockage est traduite (type, taille, droits) : jamais le texte brut du serveur seul.
    const { error } = await this.sb.storage.from('signature-assets').upload(plan.path, new Blob([plan.bytes as BlobPart], { type: plan.contentType }), {
        contentType: plan.contentType,
        cacheControl: ASSET_CACHE_CONTROL,
        // Un GIF est nommé par le hash de son contenu, une icône teintée par sa teinte : même nom = même fichier.
        upsert: plan.kind === 'gif' || plan.upsert === true,
      });
    if (error) throw new Error(explainStorageError(error.message, plan.bytes.length));
    // URL sur NOTRE domaine (réécriture /a/* -> Storage) : les signatures ne dépendent pas de l'hébergeur.
    return config.publicBaseUrl
      ? `${publicBaseUrl}/a/${plan.path}`
      : this.sb.storage.from('signature-assets').getPublicUrl(plan.path).data.publicUrl;
  }
  private async invoke(name: string, body: unknown) {
    const { data, error } = await this.sb.functions.invoke(name, { body });
    if (error) {
      let detail = error.message;
      try {
        detail = (await error.context?.json())?.error ?? detail;
      } catch { /* corps non JSON */ }
      throw new Error(detail);
    }
    return data;
  }
  sync(dryRun: boolean) {
    return this.invoke('sync-directory', { dryRun });
  }
  resync(opts: { dryRun: boolean; only?: string[] }) {
    return this.invoke('sync-directory', { mode: 'resync', dryRun: opts.dryRun, only: opts.only }) as Promise<ResyncReport>;
  }
  push(opts: { dryRun?: boolean; only?: string[]; force?: boolean }) {
    return this.invoke('push-signatures', opts);
  }
  listRuns() {
    return this.q<Run[]>(
      this.sb.from('runs').select('*').order('started_at', { ascending: false })
        .limit(30),
    );
  }
  async automationStatus() {
    return (await this.q<AutomationStatus>(this.sb.rpc('automation_status'))) ?? { auto_push: false, pg_cron: false, cron_jobs: 0 };
  }
  sendInstallLinks(opts: { dryRun?: boolean; only?: string[] }) {
    return this.invoke('send-install-links', opts) as Promise<InstallMailReport>;
  }
  async clickStats() {
    return await this.q<ClickStats>(this.sb.rpc('click_stats'));
  }
  listEdgeHits(days = 14) {
    const since = new Date(Date.now() - days * 864e5).toISOString().slice(0, 10);
    return this.q<EdgeHit[]>(this.sb.from('edge_hits').select('*').gte('day', since).order('day', { ascending: false }));
  }
  listStates() {
    return this.q<SigState[]>(
      this.sb.from('signature_state').select('*').order('send_as_email'),
    );
  }
  async clicksByCampaign() {
    const rows = await this.q<{ id: string; clicks: number }[]>(
      this.sb.from('campaign_stats').select('id, clicks'),
    );
    return Object.fromEntries(rows.map((r) => [r.id, Number(r.clicks)]));
  }
  async getAutoPush() {
    const row = await this.q<{ auto_push: boolean } | null>(
      this.sb.from('settings').select('auto_push').eq('id', 1).maybeSingle(),
    );
    return Boolean(row?.auto_push);
  }
  async setAutoPush(on: boolean) {
    await this.q(
      this.sb.from('settings').update({ auto_push: on }).eq('id', 1),
    );
  }
  install(token: string): Promise<InstallPayload> {
    return fetchInstall(token);
  }
}

/**
 * Page d'installation, sans supabase-js : un seul fetch vers la fonction publique, avec délai maximal.
 * La page ne dépend ainsi ni du CDN (jsdelivr) ni d'une session : si l'appel traîne ou échoue, on le
 * dit au lieu de laisser une page blanche.
 */
export async function fetchInstall(token: string, timeoutMs = 25_000): Promise<InstallPayload> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const res = await fetch(`${config.supabaseUrl}/functions/v1/install?t=${encodeURIComponent(token)}`, { signal: ctl.signal });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error ?? `Erreur ${res.status}`);
    return data;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw new Error(`Le serveur n’a pas répondu en ${Math.round(timeoutMs / 1000)} secondes. Rechargez la page ; si cela se reproduit, vérifiez la connexion ou prévenez l’administrateur.`);
    if (e instanceof TypeError) throw new Error('Impossible de joindre le serveur des signatures (connexion coupée, ou domaine bloqué par un filtre réseau). Rechargez la page ou essayez depuis un autre réseau.');
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

/** Ce dont la page d'installation a besoin : rien d'autre que l'appel d'installation. */
export const installApi = (): Pick<Api, 'install'> => (isDemo ? new DemoApi() : { install: fetchInstall });

export async function createApi(): Promise<Api> {
  return isDemo ? new DemoApi() : await LiveApi.create();
}
